<template>
  <transition name="fade" mode="out-in">
    <div class="wrapper" :key="spaceStore.currentSpace?.workspace_id">
      <Header
        v-bind="spaceStore.currentSpace"
        :buttons="[
          {
            class: 'outline secondary icon icon-left',
            label: 'Paramètres de l\'espace',
            icon: 'bi-gear',
            click: () => {
              state.modalIsOpen = true;
            },
          },
          {
            class: 'primary icon icon-left',
            label: 'Créer une campagne',
            icon: 'bi-plus-lg',
            routeParams: {
              name: 'newCampaign',
              query: {
                spaceId: route.params.spaceId,
              },
            },
          },
        ]"
      />
      <div class="data-card card">
        <h4>Les différentes campagnes de cet espace</h4>
        <p>
          Vous pouvez sélectionner la campagne de votre choix en cliquant sur
          son nom.
        </p>
        <Table
          :data="spaceCampaigns"
          :fields="state.fields"
          @clickOnRow="
            (campaign) =>
              $router.push({
                name: 'campaign',
                params: {
                  campaignId: campaign.campaign_id,
                },
              })
          "
        >
          <template #status="{ data }">
            <div
              class="tag"
              :class="data.status ? 'green' : 'grey-light'"
              v-text="data.status ? 'Terminé' : 'En cours'"
            />
          </template>
          <template #creation_time="{ data }">
            <div
              :style="{ width: 'fit-content' }"
              v-text="getDate(data.creation_time, 'D MMM YYYY')"
              v-tooltip="{
                content: getDate(data.creation_time, 'D MMM YYYY à HH[h]mm'),
                placement: 'top',
              }"
            />
          </template>
          <template #actions="{ row }">
            <Button
              label="Paramètres"
              class="secondary outline icon"
              icon="bi-gear"
              @click="
                (state.showParameters = true), (state.selectedCampaign = row)
              "
            />
            <div @click.stop="null">
              <VDropdown :distance="10" placement="left">
                <div class="btn outline secondary icon square">
                  <i class="bi bi-three-dots-vertical" />
                </div>
                <template #popper>
                  <div class="action" v-close-popper @click="archive(row)">
                    <i class="bi bi-arrow-counterclockwise" />
                    <span>Archiver la campagne</span>
                  </div>
                </template>
              </VDropdown>
            </div>
          </template>
          <template #empty>
            <Empty
              message="Aucune campagne."
              :action="{
                label: 'Créer une campagne',
                routeParams: {
                  name: 'newCampaign',
                  query: {
                    spaceId: route.params.spaceId,
                  },
                },
              }"
              :dark="isDark"
              icon="box"
            />
          </template>
        </Table>
      </div>
      <SpaceModal
        :isOpen="state.modalIsOpen"
        :space="spaceStore.currentSpace"
        action="edit"
        @close="(state.modalIsOpen = false), (state.space = null)"
      />
      <ActionModal
        modal="archive"
        :data="state.selectedCampaign"
        :isOpen="state.showActionModal"
        @close="state.showActionModal = false"
        @confirm="state.showActionModal = false"
      />
      <ParametersModal
        :isOpen="state.showParameters"
        :campaign="state.selectedCampaign"
        @close="
          (state.showParameters = !state.showParameters),
            (state.selectedCampaign = {})
        "
      />
    </div>
  </transition>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { reactive, computed } from "vue";
import { useSpaceStore } from "@/stores/space.js";
import { useCampaignStore } from "@/stores/campaign.js";
import { useRoute } from "vue-router";
import { useDark } from "@vueuse/core";
import { getDate, dateIsBefore } from "@/composables/utils.js";
import Header from "@/components/Header.vue";
import Empty from "@/components/table/Empty.vue";
import SpaceModal from "@/components/modals/app/SpaceModal.vue";
import ParametersModal from "@/components/modals/app/parametersModal/ParametersModal.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";

const isDark = useDark();

const route = useRoute();

const spaceStore = useSpaceStore();

const campaignStore = useCampaignStore();

const { campaigns } = storeToRefs(campaignStore);

const state = reactive({
  selectedCampaign: {},
  modalIsOpen: false,
  showParameters: false,
  showActionModal: false,
  campaign: null,
  fields: [
    {
      id: "name",
      label: "Nom de la campagne",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "public_name",
      label: "Nom de l'organisation",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "job_title",
      label: "Poste",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "creation_time",
      label: "Date de création",
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
  ],
});

if (!spaceStore.currentSpace) {
  await spaceStore.getSpace(route.params.companyId, route.params.spaceId);
}

if (!spaceStore.currentSpace.fetchedCampaigns) {
  await campaignStore.getCampaigns(
    route.params.companyId,
    route.params.spaceId
  );
}

const spaceCampaigns = computed(() => {
  return campaigns.value.filter(
    (campaign) =>
      campaign.workspace_id === route.params.spaceId && !campaign.archived
  );
});

async function archive(campaign) {
  state.selectedCampaign = campaign;
  state.showActionModal = true;
}
</script>
